<template>
  <div class="collection-instock">
    <div class="collection-instock__groups" v-if="optionGroups.length">
      <CollectionInStockGroup
        v-for="(group, index) in optionGroups"
        class="collection-instock__group"
        :key="`option-group_${index}`"
        :group="group"
      />
    </div>
    <div class="collection-instock__loading" v-else>Loading In Stock Products...</div>
  </div>
</template>

<script lang="ts" setup>
import _, { Dictionary } from 'lodash'
import { computed, onMounted, ref, unref } from 'vue'
import CollectionInStockGroup from './CollectionInStockGroup.vue'

import {
  DEFAULT_COUNTRY_CODE,
  fetchCollectionWithProducts,
  ProductFieldsFragment,
  VariantFieldsFragment,
} from '_library'

export type CollectionInStockGroupType = {
  groupOptions: string[]
  products: (ProductFieldsFragment & {
    imageSrc: string
    groupedOptions: {
      options: string[][]
      prices: number[]
    }[]
  })[]
}

const mapEdges = (object: any) => {
  return object.edges.map((edge: any) => edge.node)
}

const products = ref<ProductFieldsFragment[]>([])

onMounted(async () => {
  const collection = await fetchCollectionWithProducts({
    handle: 'in-stock',
    countryCode: DEFAULT_COUNTRY_CODE,
  })

  products.value = collection.products
})

/**
 * Optional display-only options for products that don't have
 * real Shopify variant options.
 *
 * The names here intentionally match the real Shopify option names
 * so these products get grouped alongside products with real variants.
 */
const getInStockMetafieldOptions = (product: ProductFieldsFragment) => {
  return [
    {
      name: 'Cord Style',
      value: product.inStockCordStyle?.value,
    },
    {
      name: 'Cord Color',
      value: product.inStockCordColor?.value,
    },
    {
      name: 'Metal',
      value: product.inStockMetal?.value,
    },
  ].filter(
    (option): option is { name: string; value: string } =>
      typeof option.value === 'string' && option.value.trim().length > 0
  )
}

/**
 * Shopify creates "Title / Default Title" for products without
 * actual variant options. Treat that as having no real options.
 */
const hasRealProductOptions = (product: ProductFieldsFragment) => {
  return product.options.some((option) => option?.name !== 'Title')
}

/**
 * Returns the option names that should determine which In Stock
 * group this product belongs to.
 */
const getIndexOptionNames = (product: ProductFieldsFragment) => {
  if (hasRealProductOptions(product)) {
    return product.options
      .map((option) => option?.name)
      .filter((name): name is string => Boolean(name) && name !== 'Title')
  }

  return getInStockMetafieldOptions(product).map((option) => option.name)
}

const productsWithInStockVariants = computed(() => {
  return unref(products.value)
    .map((product: ProductFieldsFragment) => {
      const variants = mapEdges(product.variants)

      return {
        ...product,
        variants: variants.filter((variant: VariantFieldsFragment) => {
          return variant.availableForSale && (variant?.quantityAvailable || 0) > 0
        }),
      }
    })
    .filter((product) => product.variants.length > 0)
})

const variantGroupedProducts = computed(() => {
  const _products = productsWithInStockVariants.value

  return _products.map((product) => {
    const variants = unref(product.variants) as VariantFieldsFragment[]
    const media = unref(mapEdges(product.media))

    /**
     * If this product doesn't have genuine Shopify options,
     * construct its index display options from the metafields.
     */
    if (!hasRealProductOptions(product)) {
      const metafieldOptions = getInStockMetafieldOptions(product)

      const prices = variants
        .map((variant) => parseFloat(variant.price.amount) * 100)
        .filter((price, i, self) => self.indexOf(price) === i)

      const groupedOptions = metafieldOptions.length
        ? [
            {
              options: metafieldOptions.map((option) => [option.value]),
              prices,
            },
          ]
        : [
            {
              options: [],
              prices,
            },
          ]

      return {
        ...product,
        imageSrc: media?.[0]?.previewImage?.thumbUrl,
        groupedOptions,
      }
    }

    /**
     * Existing behavior for products with real Shopify variants.
     */
    const variantOptions: Dictionary<VariantFieldsFragment[]> = _.groupBy(
      variants,
      (variant) => variant?.selectedOptions[0].name
    )

    const variantOptionKeys = Object.keys(variantOptions)

    const groupedOptions = variantOptionKeys.reduce((c: any, key) => {
      const group = variantOptions[key]

      // Get prices
      const prices = group
        .map((variant) => variant.price)
        .filter((price, i, self) => self.indexOf(price) === i)
        .map((price) => parseFloat(price.amount) * 100)

      // Format Options
      const options = group
        .map((variant) => variant.selectedOptions)
        .map((options) => options.map((option) => [option.value]))
        .reduce((acc, curr) => acc.map((o, i) => o.concat(curr[i]), []))
        .map((a) => a.filter((a, i, self) => self.indexOf(a) === i))

      return [
        ...c,
        {
          options,
          prices,
        },
      ]
    }, [])

    return {
      ...product,
      imageSrc: media?.[0]?.previewImage?.thumbUrl,
      groupedOptions,
    }
  })
})

const optionGroups = computed(() => {
  const productOptionsToString = (product: ProductFieldsFragment) => {
    return getIndexOptionNames(product)
      .map((name) => name.trim().toLowerCase())
      .join(',')
  }

  const groups = _.groupBy(variantGroupedProducts.value, productOptionsToString)

  return Object.keys(groups).map((key) => {
    const groupProducts = groups[key]

    // Use the first product's actual option names for display,
    // while the normalized names above are only used for grouping.
    const groupOptions = getIndexOptionNames(groupProducts[0]).map((name) => name.trim())

    return {
      groupOptions,
      products: groupProducts,
    } as CollectionInStockGroupType
  })
})
</script>

<style lang="scss">
@import '~styles/base.scss';

.collection-instock {
  &__groups {
    border: 1px solid var(--color-dark);
    border-top: none;

    @include breakpoint('l') {
      border: none;
      max-width: 90vw !important;
      width: 90vw !important;
    }
  }

  &__loading {
    @include heading;
    font-size: 31px;
    text-align: center;
    padding: 100px;
  }
}
</style>
